/**
 * `Setup Progress` — the panel that sits under the configuration band (`07-configuration.md`
 * §2; `docs/design/configuration-page-v1.html`).
 *
 * The mockup draws a `.panel` whose head carries the note *`Eight Sections, In Setup Order ·
 * Weeks Before To Matchday One`*, a status pill and the `Expand All` / `Collapse All` pair,
 * over a five-figure strip: `Sections`, `Checks Passed`, `Rules In Force`, `Data Sources`,
 * `Status`.
 *
 * **Three of those five figures do not exist in this build, and the strip says so.** The
 * mockup's numbers are demonstration data. Here:
 *
 *  · `Sections` is `8` — a fact about the design, not about a tournament, and true of every
 *    installation.
 *  · `Rules In Force` is counted from the rule set actually fetched.
 *  · `Checks Passed`, `Data Sources` and `Status` have no data layer: the pre-flight
 *    checklist, the per-dataset source table and the go-live gate are all unbuilt. Each prints
 *    an em dash at the figure's own size with the reason under it, so an absent figure reads
 *    as absent. `0 / 10` would be a claim that ten checks were run and all ten failed, and
 *    `Setup` would be a claim about a lifecycle nothing on the wire reports.
 */

export interface SetupProgressPanelProps {
  /** Enabled rules over the rule set's size. Null while the rule set has not been read. */
  rulesInForce: { inForce: number; total: number } | null;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}

/** §2's figure strip, in the mockup's order. */
const SECTION_COUNT = 8;

function Figure({
  label,
  value,
  sub,
  absent = false,
}: {
  label: string;
  value: string;
  sub: string;
  absent?: boolean;
}): React.JSX.Element {
  return (
    <div className="fig">
      <span className="k">{label}</span>
      <span className={absent ? "v v--none" : "v"} data-testid={`cfg-fig-${label}`}>
        {value}
      </span>
      <span className="sub">{sub}</span>
    </div>
  );
}

export function SetupProgressPanel({
  rulesInForce,
  onExpandAll,
  onCollapseAll,
}: SetupProgressPanelProps): React.JSX.Element {
  return (
    <section className="panel panel--d1" aria-labelledby="cfgHeadTitle" data-howto="setup-progress">
      <div className="panel__head">
        <h3 className="panel__title" id="cfgHeadTitle">
          Setup Progress
        </h3>
        <span className="panel__note">
          Eight Sections, In Setup Order · Weeks Before To Matchday One
        </span>
        <span className="spacer" />
        {/*
          The mockup's pill reads `Configured · Not Yet Live` and flips to `Live · Operations
          Running` at go-live. Neither can be said here: no route reports whether this
          tournament has gone live, so the pill states what IS known — that the sections this
          build has data for are the editable ones — rather than guessing at a lifecycle.
        */}
        <span className="statuspill statuspill--muted" data-testid="cfg-status">
          <span className="sdot" aria-hidden="true" />
          Go-Live Gate Not Built
        </span>
        <div className="cfgacts">
          <button type="button" className="gbtn" onClick={onExpandAll}>
            Expand All
          </button>
          <button type="button" className="gbtn" onClick={onCollapseAll}>
            Collapse All
          </button>
        </div>
      </div>

      <div className="figrow">
        <Figure
          label="Sections"
          value={String(SECTION_COUNT)}
          sub="Identity through go-live"
        />
        <Figure
          label="Checks Passed"
          value="—"
          sub="Pre-flight verification is not built yet"
          absent
        />
        <Figure
          label="Rules In Force"
          value={
            rulesInForce === null
              ? "—"
              : `${String(rulesInForce.inForce)} / ${String(rulesInForce.total)}`
          }
          sub="Hard blocks and soft warnings"
          absent={rulesInForce === null}
        />
        <Figure
          label="Data Sources"
          value="—"
          sub="Per-dataset source is not configured here yet"
          absent
        />
        <Figure
          label="Status"
          value="—"
          sub="Nothing reports whether this tournament is live"
          absent
        />
      </div>
    </section>
  );
}
